export type StatsLabelContext =
  | 'channels'
  | 'channelsIntegers'
  | 'boxes'
  | 'registrations'
  | 'registrationsIntegers'
  | 'timeline'
  | 'performances'
  | 'performancesElapsedTime'
  | 'bouncesTypes'
  | 'bouncesTypesIntegers'
  | 'vouchers'
  | 'yesNo'
  | 'genders'
  | 'ageRangeIntegers';

const createMap = (entries: Record<string, string>): Record<string, string> => Object.entries(entries).reduce<Record<string, string>>((acc, [key, value]) => {
  acc[key.toLowerCase()] = value;
  return acc;
}, {});

const COLUMN_PREFIX = 'stats.columns.';

const toCamelCaseSegment = (segment: string): string => {
  if (!segment) {
    return segment;
  }

  const cleaned = segment.replace(/[^a-zA-Z0-9_-]/g, '');

  if (!cleaned || (!cleaned.includes('_') && !cleaned.includes('-'))) {
    return cleaned || segment;
  }

  return cleaned
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part, index) => {
      const lowerPart = part.toLowerCase();
      if (index === 0) {
        return lowerPart;
      }

      return lowerPart.charAt(0).toUpperCase() + lowerPart.slice(1);
    })
    .join('');
};

const statsLabelSlugMap: Record<string, string> = createMap({
  0: 'stats.statsLabels.genders.0',
  1: 'stats.statsLabels.genders.1',
  2: 'stats.statsLabels.genders.2',
  3: 'stats.statsLabels.bouncesTypes.3',
  4: 'stats.statsLabels.bouncesTypes.4',
  '0-11': 'stats.statsLabels.ageRangeIntegers.1',
  '12-17': 'stats.statsLabels.ageRangeIntegers.2',
  '18-24': 'stats.statsLabels.ageRangeIntegers.3',
  '25-34': 'stats.statsLabels.ageRangeIntegers.4',
  '25-39': 'stats.statsLabels.ageRangeIntegers.4',
  '35-44': 'stats.statsLabels.ageRangeIntegers.5',
  '40-59': 'stats.statsLabels.ageRangeIntegers.5',
  '45-54': 'stats.statsLabels.ageRangeIntegers.6',
  '55-64': 'stats.statsLabels.ageRangeIntegers.7',
  '60-69': 'stats.statsLabels.ageRangeIntegers.6',
  '65+': 'stats.statsLabels.ageRangeIntegers.8',
  '70+': 'stats.statsLabels.ageRangeIntegers.7',
  'less-than-5-min': 'stats.statsLabels.performances.elapsedTime.0',
  '5-to-15-min': 'stats.statsLabels.performances.elapsedTime.1',
  '15-to-30-min': 'stats.statsLabels.performances.elapsedTime.2',
  '30-to-60-min': 'stats.statsLabels.performances.elapsedTime.3',
  '1-to-4-hours': 'stats.statsLabels.performances.elapsedTime.4',
  '4-to-12-hours': 'stats.statsLabels.performances.elapsedTime.5',
  '12-to-24-hours': 'stats.statsLabels.performances.elapsedTime.6',
  '24-to-48-hours': 'stats.statsLabels.performances.elapsedTime.7',
  '2-to-5-days': 'stats.statsLabels.performances.elapsedTime.8',
  percentage: 'stats.statsLabels.performances.percentage',
  email: 'stats.statsLabels.channels.email',
  sms: 'stats.statsLabels.channels.sms',
  display: 'stats.statsLabels.channels.display',
  popup: 'stats.statsLabels.channels.popup',
  embed: 'stats.statsLabels.channels.embed',
  facebookmessenger: 'stats.statsLabels.channels.facebookmessenger',
  pushnotifications: 'stats.statsLabels.channels.pushnotifications',
  whatsapp: 'stats.statsLabels.channels.whatsapp',
  rcs: 'stats.statsLabels.channels.rcs',
  richtexting: 'stats.statsLabels.channels.rcs',
  registration: 'stats.statsLabels.registrations.registration',
  newsletter: 'stats.statsLabels.registrations.newsletter',
  sending: 'stats.statsLabels.timeline.sending',
  openings: 'stats.statsLabels.timeline.openings',
  clicks: 'stats.statsLabels.timeline.clicks',
  sales: 'stats.statsLabels.timeline.sales',
  unsubscribes: 'stats.statsLabels.timeline.unsubscribes',
  bounces: 'stats.statsLabels.timeline.bounces',
  spams: 'stats.statsLabels.timeline.spams',
  deliveries: 'stats.statsLabels.timeline.deliveries',
  pagesviews: 'stats.statsLabels.timeline.pagesViews',
  uniquevisitors: 'stats.statsLabels.timeline.uniqueVisitors',
  prospects: 'stats.statsLabels.timeline.prospects',
  customers: 'stats.statsLabels.timeline.customers',
  'hard-bounce': 'stats.statsLabels.bouncesTypesIntegers.1',
  'soft-bounce': 'stats.statsLabels.bouncesTypesIntegers.2',
  spam: 'stats.statsLabels.bouncesTypesIntegers.3',
  unsubscription: 'stats.statsLabels.bouncesTypesIntegers.4',
  unused: 'stats.statsLabels.vouchers.0',
  used: 'stats.statsLabels.vouchers.1',
  yes: 'stats.statsLabels.yesNo.1',
  no: 'stats.statsLabels.yesNo.0',
  unknown: 'stats.statsLabels.unknown',
  male: 'stats.statsLabels.genders.1',
  female: 'stats.statsLabels.genders.2',
  other: 'stats.statsLabels.genders.3',
  total: 'stats.statsLabels.common.total',
  overall: 'stats.statsLabels.common.overall',
  // Orders
  'new-customers': 'stats.statsLabels.orders.customerType.1',
  'repeat-customers': 'stats.statsLabels.orders.customerType.2',
  // Note: orders.status comes directly from DB (shops_orders_status table), not from i18n
  'no-voucher': 'stats.statsLabels.orders.voucher.0.0',
  'spm-voucher-only': 'stats.statsLabels.orders.voucher.0.1',
  'voucher-only': 'stats.statsLabels.orders.voucher.1.0',
  'voucher-and-spm-voucher': 'stats.statsLabels.orders.voucher.1.1',
  // Traffic
  'not-logged-in': 'stats.statsLabels.traffic.loginStatus.0',
  'logged-in': 'stats.statsLabels.traffic.loginStatus.1',
  'unknown-contact': 'stats.statsLabels.traffic.contactStatus.0',
  'known-contact': 'stats.statsLabels.traffic.contactStatus.1',
  // Carts
  'anonymous': 'stats.statsLabels.carts.identification.0',
  'identified': 'stats.statsLabels.carts.identification.1',
  'without-coupon': 'stats.statsLabels.carts.voucher.0',
  'with-coupon': 'stats.statsLabels.carts.voucher.1',
  'active': 'stats.statsLabels.carts.status.1',
  'abandoned': 'stats.statsLabels.carts.status.2',
  'converted': 'stats.statsLabels.carts.status.3',
});

const statsLabelContextMap: Record<StatsLabelContext, Record<string, string>> = {
  channels: createMap({
    email: 'stats.statsLabels.channels.email',
    sms: 'stats.statsLabels.channels.sms',
    display: 'stats.statsLabels.channels.display',
    popup: 'stats.statsLabels.channels.popup',
    embed: 'stats.statsLabels.channels.embed',
    facebookmessenger: 'stats.statsLabels.channels.facebookmessenger',
    pushnotifications: 'stats.statsLabels.channels.pushnotifications',
    whatsapp: 'stats.statsLabels.channels.whatsapp',
    rcs: 'stats.statsLabels.channels.rcs',
    richtexting: 'stats.statsLabels.channels.rcs',
    teaser: 'stats.statsLabels.channels.teaser',
  }),
  channelsIntegers: createMap({
    email: 'stats.statsLabels.channelsIntegers.1',
    sms: 'stats.statsLabels.channelsIntegers.2',
    popup: 'stats.statsLabels.channelsIntegers.3',
    embed: 'stats.statsLabels.channelsIntegers.4',
    facebookmessenger: 'stats.statsLabels.channelsIntegers.5',
    pushnotifications: 'stats.statsLabels.channelsIntegers.6',
    whatsapp: 'stats.statsLabels.channelsIntegers.7',
    rcs: 'stats.statsLabels.channelsIntegers.8',
    teaser: 'stats.statsLabels.channelsIntegers.9',
  }),
  boxes: createMap({
    email: 'stats.statsLabels.boxes.boxsendemail',
    sms: 'stats.statsLabels.boxes.boxsendsms',
    popup: 'stats.statsLabels.boxes.boxdisplayfreepopup',
    embed: 'stats.statsLabels.boxes.boxdisplayfreemodel',
    facebookmessenger: 'stats.statsLabels.boxes.boxsendfbmessenger',
    pushnotifications: 'stats.statsLabels.boxes.boxsendpushnotification',
    whatsapp: 'stats.statsLabels.boxes.boxsendwhatsapp',
    rcs: 'stats.statsLabels.boxes.boxsendrcs',
  }),
  registrations: createMap({
    registration: 'stats.statsLabels.registrations.registration',
    newsletter: 'stats.statsLabels.registrations.newsletter',
  }),
  registrationsIntegers: createMap({
    registration: 'stats.statsLabels.registrationsIntegers.1',
    newsletter: 'stats.statsLabels.registrationsIntegers.2',
  }),
  timeline: createMap({
    sending: 'stats.statsLabels.timeline.sending',
    openings: 'stats.statsLabels.timeline.openings',
    clicks: 'stats.statsLabels.timeline.clicks',
    sales: 'stats.statsLabels.timeline.sales',
    unsubscribes: 'stats.statsLabels.timeline.unsubscribes',
    bounces: 'stats.statsLabels.timeline.bounces',
    spams: 'stats.statsLabels.timeline.spams',
    deliveries: 'stats.statsLabels.timeline.deliveries',
    pagesviews: 'stats.statsLabels.timeline.pagesViews',
    uniquevisitors: 'stats.statsLabels.timeline.uniqueVisitors',
    prospects: 'stats.statsLabels.timeline.prospects',
    customers: 'stats.statsLabels.timeline.customers',
  }),
  performances: createMap({
    percentage: 'stats.statsLabels.performances.percentage',
  }),
  performancesElapsedTime: createMap({
    'less-than-5-min': 'stats.statsLabels.performances.elapsedTime.0',
    '5-to-15-min': 'stats.statsLabels.performances.elapsedTime.1',
    '15-to-30-min': 'stats.statsLabels.performances.elapsedTime.2',
    '30-to-60-min': 'stats.statsLabels.performances.elapsedTime.3',
    '1-to-4-hours': 'stats.statsLabels.performances.elapsedTime.4',
    '4-to-12-hours': 'stats.statsLabels.performances.elapsedTime.5',
    '12-to-24-hours': 'stats.statsLabels.performances.elapsedTime.6',
    '24-to-48-hours': 'stats.statsLabels.performances.elapsedTime.7',
    '2-to-5-days': 'stats.statsLabels.performances.elapsedTime.8',
  }),
  bouncesTypes: createMap({
    1: 'stats.statsLabels.bouncesTypes.1',
    2: 'stats.statsLabels.bouncesTypes.2',
    3: 'stats.statsLabels.bouncesTypes.3',
    4: 'stats.statsLabels.bouncesTypes.4',
  }),
  bouncesTypesIntegers: createMap({
    'hard-bounce': 'stats.statsLabels.bouncesTypesIntegers.1',
    'soft-bounce': 'stats.statsLabels.bouncesTypesIntegers.2',
    spam: 'stats.statsLabels.bouncesTypesIntegers.3',
    unsubscription: 'stats.statsLabels.bouncesTypesIntegers.4',
  }),
  vouchers: createMap({
    unused: 'stats.statsLabels.vouchers.0',
    used: 'stats.statsLabels.vouchers.1',
  }),
  yesNo: createMap({
    yes: 'stats.statsLabels.yesNo.1',
    no: 'stats.statsLabels.yesNo.0',
  }),
  genders: createMap({
    0: 'stats.statsLabels.genders.0',
    1: 'stats.statsLabels.genders.1',
    2: 'stats.statsLabels.genders.2',
    male: 'stats.statsLabels.genders.1',
    female: 'stats.statsLabels.genders.2',
    unknown: 'stats.statsLabels.genders.0',
  }),
  ageRangeIntegers: createMap({
    unknown: 'stats.statsLabels.ageRangeIntegers.0',
    '0-11': 'stats.statsLabels.ageRangeIntegers.1',
    '12-17': 'stats.statsLabels.ageRangeIntegers.2',
    '18-24': 'stats.statsLabels.ageRangeIntegers.3',
    '25-34': 'stats.statsLabels.ageRangeIntegers.4',
    '25-39': 'stats.statsLabels.ageRangeIntegers.4',
    '35-44': 'stats.statsLabels.ageRangeIntegers.5',
    '40-59': 'stats.statsLabels.ageRangeIntegers.5',
    '45-54': 'stats.statsLabels.ageRangeIntegers.6',
    '60-69': 'stats.statsLabels.ageRangeIntegers.6',
    '55-64': 'stats.statsLabels.ageRangeIntegers.7',
    '70+': 'stats.statsLabels.ageRangeIntegers.7',
    '65+': 'stats.statsLabels.ageRangeIntegers.8',
  }),
};

export const normalizeStatsLabelSlug = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).trim().toLowerCase();
};

export const normalizeStatsColumnTranslationKey = (
  value: string | null | undefined,
): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.replace(/`/g, '').trim();
  if (!trimmed) {
    return undefined;
  }

  const hasPrefix = trimmed.startsWith(COLUMN_PREFIX);
  const suffix = hasPrefix ? trimmed.slice(COLUMN_PREFIX.length) : trimmed;
  if (!suffix) {
    return undefined;
  }

  const normalizedSuffix = suffix
    .split('.')
    .map((segment) => toCamelCaseSegment(segment))
    .filter(Boolean)
    .join('.');

  if (!normalizedSuffix) {
    return undefined;
  }

  return `${COLUMN_PREFIX}${normalizedSuffix}`;
};

export const humanizeStatsColumnKey = (value: string | null | undefined): string | undefined => {
  const normalizedKey = normalizeStatsColumnTranslationKey(value);
  if (!normalizedKey) {
    return undefined;
  }

  const suffix = normalizedKey.slice(COLUMN_PREFIX.length);
  if (!suffix) {
    return undefined;
  }

  const spaced = suffix
    .replace(/\./g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!spaced) {
    return undefined;
  }

  return spaced
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const resolveStatsLabelContextFromFormat = (
  format: string | null | undefined,
): StatsLabelContext | undefined => {
  if (!format) {
    return undefined;
  }

  const normalized = format.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }

  if (normalized.includes('channel')) {
    return normalized.includes('integer') ? 'channelsIntegers' : 'channels';
  }

  if (normalized.includes('box')) {
    return 'boxes';
  }

  if (normalized.includes('registration')) {
    return normalized.includes('integer')
      ? 'registrationsIntegers'
      : 'registrations';
  }

  if (normalized.includes('age')) {
    return 'ageRangeIntegers';
  }

  if (normalized.includes('gender')) {
    return 'genders';
  }

  if (normalized.includes('voucher')) {
    return 'vouchers';
  }

  if (normalized.includes('yes')) {
    return 'yesNo';
  }

  if (normalized.includes('bounce')) {
    return normalized.includes('integer')
      ? 'bouncesTypesIntegers'
      : 'bouncesTypes';
  }

  if (normalized.includes('timeline')) {
    return 'timeline';
  }

  if (normalized.includes('performance') && normalized.includes('elapsed')) {
    return 'performancesElapsedTime';
  }

  if (normalized.includes('performance')) {
    return 'performances';
  }

  return undefined;
};

export const resolveStatsLabelContextFromField = (
  field: string | null | undefined,
): StatsLabelContext | undefined => {
  if (!field) {
    return undefined;
  }

  const normalized = field.replace(/`/g, '').trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }

  if (normalized.includes('channel')) {
    return normalized.includes('integer') ? 'channelsIntegers' : 'channels';
  }

  if (normalized.includes('box')) {
    return 'boxes';
  }

  if (normalized.includes('registration')) {
    return normalized.includes('integer')
      ? 'registrationsIntegers'
      : 'registrations';
  }

  if (normalized.includes('age')) {
    return 'ageRangeIntegers';
  }

  if (normalized.includes('gender')) {
    return 'genders';
  }

  if (normalized.includes('voucher')) {
    return 'vouchers';
  }

  if (normalized.includes('yes') || normalized.includes('no')) {
    return 'yesNo';
  }

  if (normalized.includes('bounce')) {
    return normalized.includes('integer')
      ? 'bouncesTypesIntegers'
      : 'bouncesTypes';
  }

  if (normalized.includes('timeline')) {
    return 'timeline';
  }

  if (normalized.includes('elapsed')) {
    return 'performancesElapsedTime';
  }

  if (normalized.includes('performance')) {
    return 'performances';
  }

  return undefined;
};

export const getStatsLabelKeyFromSlug = (
  value: unknown,
  context?: StatsLabelContext,
): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const raw = String(value).trim();
  if (!raw) {
    return raw;
  }

  if (raw.includes('.')) {
    return raw;
  }

  const normalized = raw.toLowerCase();

  if (context) {
    const contextMap = statsLabelContextMap[context];
    const contextMatch = contextMap?.[normalized];
    if (contextMatch) {
      return contextMatch;
    }
  }

  return statsLabelSlugMap[normalized] ?? raw;
};
